import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import ArtworkIcon from "src/assets/all-icons/artwork-icon.jsx";

import config from '@/config';
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useQuery from "@/hooks/useQuery";
import HomeHeader from '@/shared-components/home/components/HomeHeader';
import QuestionsIcon from '@/shared-components/icons/components/public/QuestionsIcon';
import Loader from '@/shared-components/loader/Loader';  // ✅ Updated import
import OrderByPopup from '@/shared-components/popups/components/OrderByPopup';
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';

import FiltersRenderer from "./components/FiltersRenderer";
import SearchFilters from './components/search-filters';
import VirtualList from './components/VirtualList';

import './index.css';

export default function HomeArtworks({ fromSearch = false, valueFromSearch = "" }) {
  const [params, setParams] = useGetSearchParams({ validParams: ["sort"] });
  const [openFilters, setOpenFilters] = useState(false);
  const originalObject = { q: "", sort: "Relevant" };
  const [query, setQuery] = useState(
    params 
      ? params 
      : originalObject
  );

  const checkIfPropertyAdded = (originalObj, updatedObj) => {
    const originalKeys = Object.keys(originalObj);
    const updatedKeys = Object.keys(updatedObj);
    return updatedKeys.filter(key => !originalKeys.includes(key));
  };

  const checkIfEmptyProperties = (properties) => {
    return properties.filter(key => {
      const value = query[key];
      return (!value || Array.isArray(value) && value.length === 0) || 
             (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) || 
             (value === "" || value === false);
    });
  };

  useEffect(() => {
    const addedKeys = checkIfPropertyAdded(originalObject, query);
    const emptyProps = checkIfEmptyProperties(addedKeys);

    emptyProps.forEach(key => {
      if (key in query) {
        delete query[key];
      }
    });
  }, [query]);

  const { loading, queryData, loadMoreData } = useQuery("home_artworks", `${config.apis.api.url}/search/artwork`, query, {
    injectToken: true,
    customKeyGetter: {
      artist: (currentValue) => currentValue.map(item => item._id),
      gallery: (currentValue) => currentValue.map(item => item._id),
      price: (currentValue) => currentValue ? [currentValue.minPrice, currentValue.maxPrice === 50000 ? Number.MAX_SAFE_INTEGER : currentValue.maxPrice] : null,
    }
  });
  
  const navigateToArtwork = useNavigateToArtwork(`home_artworks`, queryData?.data, `/search/artwork${queryData?.queryString}`);
  
  function onInput(value) {
    let newQuery = {};

    if (value === '' || !value) {
      newQuery = { ...query, q: value, sort: "Relevant" };
    } else {
      newQuery = { ...query, q: value, sort: "Default" };
    }

    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  function onSort(type) {
    const newQuery = { ...query, sort: type };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  const updateQuery = (newQuery) => {
    const cleanedQuery = cleanQuery(newQuery);
    if (Object.keys(cleanedQuery).length <= 2) {
      newQuery.sort = 'Relevant';
    } else {
      newQuery.sort = 'Default';
    }
    setQuery(newQuery);
  };

  const cleanQuery = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([, value]) => !(Array.isArray(value) && value.length === 0))
    );
  };

  useEffect(() => {
    onInput(valueFromSearch.q);
  }, [valueFromSearch.q]);

  return (
    <>
      { !fromSearch &&
        <>
          <Helmet>
            <title>Artworks | Suarte</title>
            <meta
              name="description"
              content="Get inspired by contemporary and modern art from all corners of the world and find your masterpiece. Buy and showcase with ease in the most transparent art marketplace."
            />
            <link rel="canonical" href={"https://suarte.art/artworks"} />
          </Helmet>

          <AppNavigationPage>
            <div className="home-artworks__page">
              <HomeHeader
                onInput={onInput}
                value={query.q}
                icon={<ArtworkIcon className="home-artworks__header-icon" />}
                text="Artworks"
                type="artworks"
                placeholder="Search for artworks..."
                onSort={onSort}
                defaultOption={query.sort}
              />

              {/* ✅ Updated to use the global Loader */}
              {loading ? (
                <Loader /> 
              ) : queryData?.data?.data?.length === 0 ? (
                <Text className="home-artworks__empty-text" paragraph medium>
              No artworks available for your search query.
                </Text>
              ) : (
                <VirtualList
                  key={queryData?.queryString}
                  items={queryData?.data}
                  onLoadMore={loadMoreData}
                  navigateToArtwork={navigateToArtwork}
                />
              )}
            </div>
          </AppNavigationPage>
        </>
      }

      { fromSearch &&
        <>
          <div className="home-artworks__page">
            <SearchFilters 
              query={query} 
              setQuery={updateQuery} 
              open={openFilters} 
              close={()=>setOpenFilters(false)}
            />
            {
              (valueFromSearch.q || Object.keys(query).length > 2) &&
              <>
                <div className="home-artworks__filters-flex">
                  <Text className="home-artworks__filters" paragraph onClick={()=>setOpenFilters(true)}>
                    Filters <QuestionsIcon/>
                  </Text>

                  <FiltersRenderer 
                    query={query} 
                    setQuery={updateQuery} 
                    exceptions={["sort", "q"]}
                  />
                </div>
              </>        
            }
            <div className="home-header__popup-container-fromSearch">
              {
                ((valueFromSearch.q || Object.keys(query).length > 2) && !loading) ?
                  <Text className="home-artworks__filters-with-search" large paragraph>
                    {queryData.data.totalDocs} results {valueFromSearch.q && <>for <span>{valueFromSearch.q}</span></>}
                  </Text>
                  : (!loading) ?
                    <>
                      <Text className="home-artworks__filters" paragraph onClick={()=>setOpenFilters(true)}>
                      Filters <QuestionsIcon/>
                      </Text>

                      <FiltersRenderer 
                        query={query} 
                        setQuery={updateQuery} 
                        exceptions={["sort", "q"]}
                      />
                    </>
                    : ""
              }
              {
                ((valueFromSearch.q || Object.keys(query).length > 2)) ?
                  <OrderByPopup 
                    defaultOption={query.sort}
                    options={["Relevant", "Default", "Recent", "High to low", "Low to high"]} 
                    onChangeOption={onSort}
                  /> 
                  :
                  <OrderByPopup 
                    defaultOption={query.sort}
                    options={["Relevant", "Recent", "High to low", "Low to high"]} 
                    onChangeOption={onSort}
                  /> 
              }
            </div>
          
            { loading ? (
              <Loader />
            ) : queryData.data.data.length === 0 ? (
              <Text className="home-artworks__empty-text" paragraph medium>
                No artworks available for your search query.
              </Text>
            ) : (
              <VirtualList
                key={queryData.queryString}
                items={queryData.data}
                onLoadMore={loadMoreData}
                navigateToArtwork={navigateToArtwork}
                fromSearch={fromSearch}
              />
            )}
          </div>
        </>
      }
    </>
  );
}
